import i18n from 'i18next';
import { autorun, reaction } from 'mobx';
import { useObserver } from 'mobx-react-lite';
import PropTypes from 'prop-types';
import React from 'react';
import { Rnd } from 'react-rnd';
import Transition from 'react-transition-group/Transition';
import styled, { css } from 'styled-components';

import { useLockBodyScroll } from '../../hooks';
import { useStores } from '../../stores';
import { getFontSize } from '../../utilities';

const ChatWindow = ({ children }) => {
  const { config, chatWindow, app, ui } = useStores();
  useLockBodyScroll({ enable: chatWindow.isFullScreen });

  React.useEffect(
    () => {
      reaction(
        () => chatWindow.isFullscreenVariant,
        isFullscreenVariant => {
          if (isFullscreenVariant) {
            chatWindow.setFullScreen();
          }
        }
      );

      autorun(() => {
        if (!chatWindow.isClosed && !config.projectSelector?.enabled) {
          app.startSession();
          localStorage.setItem('MesIndexCount', 0);
        } else if (
          !chatWindow.isClosed &&
          config.projectSelector.enabled &&
          window.location.href.toLowerCase().indexOf(config.projectSelector.detectURL1) > 0
        ) {
          ui.setProjectId(1, config.projectSelector.projectID1, config.projectSelector.source1);
        } else if (
          !chatWindow.isClosed &&
          config.projectSelector.enabled &&
          window.location.href.toLowerCase().indexOf(config.projectSelector.detectURL2) > 0
        ) {
          ui.setProjectId(2, config.projectSelector.projectID2, config.projectSelector.source2);
        }

        if (chatWindow.isClosed && app.sessionId) {
          app.updateSession();
          localStorage.setItem('MesIndexCount', 0);
        }
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  i18n.on('languageChanged', () => {
    app.setIsReadyLanguage(true);
  });

  return useObserver(() => (
    <Root
      isFixedPosition={chatWindow.isPopIn || chatWindow.isFullscreenVariant}
      fontFamily={config.fontFamily}
      textFontSize={getFontSize(config.message)}
      baseColor={config.window.colors.main}
      accentColor={config.window.colors.accent}
      buttonColor={config.button.color}
      buttonHoverColor={config.button.hoverColor}
      buttonBorderRadius={config.button.borderRadius}
    >
      <Transition
        in={chatWindow.isOpened}
        onEnter={chatWindow.onEnter}
        onEntered={chatWindow.onEntered}
        onExit={chatWindow.onExit}
        onExited={chatWindow.onExited}
        timeout={chatWindow.animationTimeout}
      >
        <div>
          <Rnd
            cancel=".react-rnd-draggable-cancel"
            dragHandleClassName="react-rnd-draggable"
            style={{
              visibility: chatWindow.isVisible ? 'visible' : 'hidden',
              position: chatWindow.isEmbedded ? 'relative' : 'fixed',
              width: chatWindow.dimensions.width,
              height: chatWindow.dimensions.height
            }}
            bounds={chatWindow.isEmbedded ? 'parent' : 'window'}
            minWidth={ui.isMobile ? '100%' : chatWindow.dimensions.minWidth}
            minHeight={ui.isMobile ? '100%' : chatWindow.dimensions.minHeight}
            disableDragging={!chatWindow.isDraggable}
            enableResizing={chatWindow.resizingConfig}
            resizeHandleStyles={chatWindow.resizeHandleStyles}
            size={{
              width: chatWindow.dimensions.width,
              height: chatWindow.dimensions.height
            }}
            position={{
              x: chatWindow.dimensions.x,
              y: chatWindow.dimensions.y
            }}
            onDragStop={chatWindow.onDragStop}
            onResizeStop={chatWindow.onResizeStop}
          >
            {children}
          </Rnd>
        </div>
      </Transition>
    </Root>
  ));
};

ChatWindow.propTypes = {
  children: PropTypes.node.isRequired
};

const Root = styled.div`
  display: block;
  width: 100%;
  height: auto;

  ${({ isFixedPosition }) =>
    isFixedPosition &&
    css`
      position: fixed;
      top: 0;
      left: 0;
      width: 0;
      height: 0;
      background-color: transparent;
      z-index: 9999999999;
    `}

  /**
    reset styles
  */
  * {
    font-family: ${props => props.fontFamily};
    box-sizing: border-box;
  }

  main {
    padding: 0 !important;
    margin: 0 !important;
    overflow: visible !important;
  }

  p {
    margin: 0;
    font-size: ${props => props.textFontSize}px;
    line-height: inherit;
    color: inherit;
  }

  input,
  textarea {
    color: ${props => props.baseColor};
  }

  a:focus,
  button:focus,
  input:focus,
  textarea:focus {
    outline: none;
  }

  form {
    min-width: auto;
    min-height: auto;
  }

  .livechat-trigger {
    padding: 0;
    font-size: 14px;
    line-height: 20px;
    text-decoration: underline;
    color: #2873c0;
    background: transparent;
    border: none;
    outline: none;
    cursor: pointer;
  }
  .livechat-trigger:disabled {
    opacity: 0.5;
    cursor: auto;
  }

  /* START special styles for DF */
  .df-button {
    position: relative;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    width: auto;
    height: auto;
    padding: 8px 16px;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: ${({ buttonColor }) => `${buttonColor}`} !important;
    background-color: transparent;
    border: 1px solid ${({ buttonColor }) => `${buttonColor}`};
    border-radius: ${({ buttonBorderRadius }) => `${buttonBorderRadius}px`};
    transition: color 0.2s ease-in-out, background-color 0.3s ease-in-out;
    outline: none;
    cursor: pointer;
  }
  .df-button:hover {
    color: ${({ buttonHoverColor }) => `${buttonHoverColor}`} !important;
    background-color: ${({ buttonColor }) => `${buttonColor}`};
  }
  a.df-button,
  a.df-button:link {
    text-decoration: none;
    color: ${({ buttonColor }) => `${buttonColor}`} !important;
  }
  a.df-button:hover,
  a.df-button:focus {
    color: ${({ buttonHoverColor }) => `${buttonHoverColor}`} !important;
    background-color: ${({ buttonColor }) => `${buttonColor}`};
  }
  .df-title {
    margin: 0 0 8px !important;
    font-size: 14px !important;
    font-weight: bold;
    color: ${({ accentColor }) => accentColor} !important;
    line-height: 1.2;
  }
  .df-subtitle {
    font-size: 14px !important;
    font-weight: bold;
    line-height: 18px;
    color: ${({ baseColor }) => baseColor} !important;
  }
  /* END special styles for DF */
`;

export default ChatWindow;
